import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { MeterPointChangeSignalState } from '../../../../../meterpoint-change-request.types';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { TranslatePipe } from '@ngx-translate/core';
import { getFormattedAddressPostCodeAndStreet } from '@mwe/utils';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { VertragspartnerSelectionType } from '../../../../new-contract-partner.utils';
import { CustomValidators } from '@shared-ikp/utils/validators';
import { EMPTY_VALUE } from '../../../../../meter-point-change-request.utils';

@Component({
  selector: 'ikp-own-company-information',
  templateUrl: './own-company-information.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ReactiveFormsModule],
})
export class OwnCompanyInformationComponent {
  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);
  private formBuilder = inject(FormBuilder);

  protected uidControl = this.formBuilder.control('', [CustomValidators.optionalUidValidator]);

  protected readonly isAlternativePartner = computed(
    () => this.wizardStore.state().newContractPartner?.vertragspartnerSelection === VertragspartnerSelectionType.ALTERNATIVE_PARTNER,
  );

  protected readonly companyInfo = computed(() => {
    const state = this.wizardStore.state().newContractPartner;
    const company = state?.currentCompanyInfo;

    return {
      name: company?.name,
      gpNr: company?.gpNr,
      address: company?.address
        ? {
            plz: company.address.postcode,
            city: company.address.city,
            street: company.address.street,
            houseNumberStaircase: company.address.streetNumber,
            door: company.address.doorNumber,
          }
        : null,
    };
  });

  protected readonly addressFormatted = computed(() => {
    const address = this.companyInfo()?.address;
    return getFormattedAddressPostCodeAndStreet({
      postcode: address?.plz,
      city: address?.city,
      street: address?.street,
      doorNumber: address?.door,
      streetNumber: address?.houseNumberStaircase,
      block: undefined,
      country: undefined,
      id: undefined,
    });
  });

  private uidInitialized = false;

  constructor() {
    effect(() => {
      const state = this.wizardStore.state().newContractPartner;
      const uid = this.isAlternativePartner() ? state?.currentCompanyInfo?.uid : state?.emptyPartner?.uid;

      if (uid && !this.uidInitialized) {
        this.uidControl.setValue(uid);
        this.uidInitialized = true;
      }
    });
  }

  protected isUidInvalid(): boolean {
    return this.uidControl.touched && this.uidControl.invalid;
  }

  protected getUidError(): string | null {
    if (this.uidControl.errors) {
      return this.uidControl.errors['translationKey'];
    }
    return null;
  }

  public get validForSubmission(): boolean {
    this.uidControl.markAsTouched();
    this.uidControl.updateValueAndValidity();

    if (this.uidControl.invalid) {
      return false;
    }

    const info = this.companyInfo();
    const currentState = this.wizardStore.state().newContractPartner;
    const uid = this.uidControl.value?.trim() || undefined;

    if (this.isAlternativePartner()) {
      if (info?.name && info?.address) {
        this.wizardStore.patchState({
          newContractPartner: {
            ...currentState,
            alternativeInvoicingAddress: {
              company: info.name,
              address: info.address,
              gpNr: info.gpNr,
              uid,
            },
          },
        });
      }
    } else {
      this.wizardStore.patchState({
        newContractPartner: {
          ...currentState,
          emptyPartner: {
            ...currentState.emptyPartner,
            uid,
          },
        },
      });
    }
    return true;
  }

  protected readonly EMPTY_VALUE = EMPTY_VALUE;
}
